import { Theme, css } from '@emotion/react';

import { PropsWithChildren, ReactNode, RefObject, forwardRef } from 'react';

import { useCloseOverlay } from '@stores/useOverlayStore';
import { Radius, Spacing } from 'chatie-design-system';

import Button, { ButtonProps } from '@components/common/Button';
import Overlay, {
  OverlayClose,
  OverlayProps,
} from '@components/common/Overlay';

import { MODAL_HEADER_HEIGHT, safeArea } from '@styles/css';
import { OnClickProps } from '@utils/misc';

const modalWrapCSS = css`
  display: flex;
  align-items: center;
  padding-left: ${Spacing.medium}px;
  padding-right: ${Spacing.medium}px;
  ${safeArea('top', 'padding', 16)}
  ${safeArea('bottom', 'padding', Spacing.medium)};
`;

export const modalCSS = (isOpen: boolean) => (theme: Theme) =>
  css`
    position: relative;
    border-radius: ${Radius.large}px;
    background-color: ${theme.overlay.bg};
    color: ${theme.var.black};
    width: 100%;
    max-width: 480px;
    max-height: 100%;
    margin: 0 auto;
    transition: 0.3s;
    will-change: transform;
    overflow-x: hidden;

    ${isOpen
      ? css`
          transform: scale(1);
          visibility: visible;
        `
      : css`
          transition: 0.25s;
          transform: scale(0.4);
          opacity: 0;
          visibility: hidden;
        `}
  `;

const headerCSS = (hideHeaderBorder: boolean) => (theme: Theme) =>
  css`
    position: relative;
    justify-content: space-between;
    padding: 0 ${Spacing.medium}px;
    height: ${MODAL_HEADER_HEIGHT}px;

    background-color: ${theme.overlay.bg};
    border-bottom: ${hideHeaderBorder
      ? '0'
      : `1px solid ${theme.border.light}`};

    h4 {
      text-align: center;
      line-height: ${MODAL_HEADER_HEIGHT}px;
    }

    button {
      position: absolute;
      top: 10px;
      right: ${Spacing.small}px;
    }
  `;

export const contentCSS = css`
  overflow: auto;
  padding: ${Spacing.medium}px;
  padding-top: ${Spacing.xlarge}px;
`;

type Props = {
  title?: string | ReactNode;
  hideCloseButton?: boolean;
  ref?: RefObject<HTMLDivElement>;
  hideHeaderBorder?: boolean;
  header?: boolean;
};

export type ModalProps = OverlayProps;

const Modal = ({
  title,
  children,
  hideCloseButton = false,
  hideHeaderBorder = false,
  header = true,
  ...props
}: PropsWithChildren<Props> & ModalProps) => {
  const { open, ref } = props;
  return (
    <Overlay css={modalWrapCSS} {...props}>
      <div css={modalCSS(open)} className="modal-container" ref={ref}>
        {header && (
          <header css={headerCSS(hideHeaderBorder)}>
            <h4>{title}</h4>
            {!hideCloseButton && <OverlayClose {...props} />}
          </header>
        )}
        <div css={contentCSS}>{children}</div>
      </div>
    </Overlay>
  );
};

export default Modal;

/** Button */

const modalButtonCSS = css`
  margin-top: ${Spacing.xlarge}px;

  & + & {
    margin-top: ${Spacing.medium}px;
  }
`;

export const ModalButton = ({
  onClick,
  className,
  ...props
}: ButtonProps & OnClickProps) => {
  const closeOverlay = useCloseOverlay();

  return (
    <div css={modalButtonCSS} suppressHydrationWarning={true}>
      <Button
        className={className}
        {...props}
        onClick={onClick ?? (() => closeOverlay())}
      />
    </div>
  );
};

export const RefModal = forwardRef<
  HTMLDivElement,
  PropsWithChildren<Props> & ModalProps
>(
  (
    {
      title,
      children,
      hideCloseButton = false,
      hideHeaderBorder = false,
      header = true,
      ...props
    },
    ref // forwardRef로 전달된 ref
  ) => {
    const { open } = props;

    return (
      <Overlay css={modalWrapCSS} {...props}>
        <div css={modalCSS(open)} className="modal-container" ref={ref}>
          {header && (
            <header css={headerCSS(hideHeaderBorder)}>
              <h4>{title}</h4>
              {!hideCloseButton && <OverlayClose {...props} />}
            </header>
          )}
          <div css={contentCSS}>{children}</div>
        </div>
      </Overlay>
    );
  }
);

RefModal.displayName = 'RefModal';
